import React, { useEffect, useMemo, useState } from 'react';
import { BookOpenText, ChevronDown } from 'lucide-react';
import { fetchLessonTopics } from '../../api/fetchLessons';
import { useAppConfig } from '../../context/AppConfigContext';
import LessonScreen from '../Lessons/LessonScreen';
import styles from './PathLessons.module.css';

import dailyImg from '../../assets/images/500x500/daily.png';
import workImg from '../../assets/images/500x500/work.png';
import foodAndDrinksImg from '../../assets/images/500x500/food_and_drinks.png';
import clothingImg from '../../assets/images/500x500/clothing.png';
import transportImg from '../../assets/images/500x500/transport.png';
import weatherImg from '../../assets/images/500x500/weather.png';
import doctorImg from '../../assets/images/500x500/doctor.png';
import inTheCityImg from '../../assets/images/500x500/in_the_city.png';
import vacationImg from '../../assets/images/500x500/vacation.png';
import supermarketImg from '../../assets/images/500x500/supermarket.png';
import bankAndMoneyImg from '../../assets/images/500x500/bank_and_money.png';
import humanBodyImg from '../../assets/images/500x500/human_body.png';
import familyImg from '../../assets/images/500x500/family.png';
import natureImg from '../../assets/images/500x500/nature.png';
import animalsAndBirdsImg from '../../assets/images/500x500/animals_and_birds.png';

const TOPIC_IMAGES = {
  daily: dailyImg,
  work: workImg,
  food_and_drinks: foodAndDrinksImg,
  clothing: clothingImg,
  transport: transportImg,
  weather: weatherImg,
  doctor: doctorImg,
  in_the_city: inTheCityImg,
  vacation: vacationImg,
  supermarket: supermarketImg,
  bank_and_money: bankAndMoneyImg,
  human_body: humanBodyImg,
  family: familyImg,
  nature: natureImg,
  animals_and_birds: animalsAndBirdsImg,
};

const getPathLessonsCopy = (t) => {
  const path = t?.path || {};

  return {
    title: path.lessons_title || 'Lessons',
    subtitle: path.lessons_subtitle || 'Choose a topic and then a subtopic.',
    badge: path.lessons_badge || 'Lessons',
    open: path.lessons_open || 'Open',
    start: path.lessons_start || 'Start',
    continue: path.lessons_continue || 'Continue',
    completed: path.lessons_completed || 'Completed',
    progress: path.lessons_progress || 'Completed subtopics',
    empty: path.lessons_empty || 'No lessons found',
    themes: path.lessons_themes || 'Themes',
    subthemes: path.lessons_subthemes || 'Subtopics',
    resume: path.lessons_resume || 'Open next subtopic',
    tierBronze: path.lessons_tier_bronze || 'Bronze',
    tierSilver: path.lessons_tier_silver || 'Silver',
    tierGold: path.lessons_tier_gold || 'Gold',
    back: path.pathLearn_back || 'Back',
    loading: path.loading || 'Loading...',
  };
};

const sumProgressEntries = (completedBlockIds) =>
  Object.values(completedBlockIds || {}).reduce((sum, value) => sum + (Number(value) || 0), 0);

const getLessonStats = (lesson) => {
  const blocks = Array.isArray(lesson?.blocks) ? lesson.blocks : [];
  const completedBlockIds = lesson?.completed_block_ids || {};
  const completedBlocks = blocks.filter((block) => Number(completedBlockIds?.[block.id]) > 0).length;
  const progressEntries = sumProgressEntries(completedBlockIds);
  const totalBlocks = blocks.length;
  const nextBlock =
    blocks.find((block) => Number(completedBlockIds?.[block.id]) <= 0) ||
    blocks[0] ||
    null;

  return {
    totalBlocks,
    completedBlocks,
    progressEntries,
    percent: totalBlocks > 0 ? Math.round((completedBlocks / totalBlocks) * 100) : 0,
    isCompleted: totalBlocks > 0 && completedBlocks >= totalBlocks,
    hasProgress: progressEntries > 0,
    nextBlock,
  };
};

const getBlockTier = (completionCount) => {
  if (completionCount >= 3) return 'gold';
  if (completionCount === 2) return 'silver';
  if (completionCount === 1) return 'bronze';
  return null;
};

const getTierLabel = (tier, copy) => {
  if (tier === 'gold') return copy.tierGold;
  if (tier === 'silver') return copy.tierSilver;
  if (tier === 'bronze') return copy.tierBronze;
  return '';
};

const getDefaultExpandedLessonId = (lessons) => {
  const inProgressLesson = lessons.find((lesson) => {
    const stats = getLessonStats(lesson);
    return stats.hasProgress && !stats.isCompleted;
  });
  if (inProgressLesson) return inProgressLesson.id;

  const firstIncompleteLesson = lessons.find((lesson) => !getLessonStats(lesson).isCompleted);
  if (firstIncompleteLesson) return firstIncompleteLesson.id;

  return lessons[0]?.id || null;
};

const PathLessons = ({ userId, onBack, onComplete, audioDisabled }) => {
  const { t } = useAppConfig();
  const copy = useMemo(() => getPathLessonsCopy(t), [t]);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLessonId, setExpandedLessonId] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        setLoading(true);
        const result = await fetchLessonTopics({ userId });
        if (!active) return;
        const nextLessons = Array.isArray(result) ? result : [];
        setLessons(nextLessons);
        setExpandedLessonId(getDefaultExpandedLessonId(nextLessons));
      } catch (_) {
        if (active) {
          setLessons([]);
          setExpandedLessonId(null);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    if (userId) {
      load();
    } else {
      setLessons([]);
      setExpandedLessonId(null);
      setLoading(false);
    }

    return () => {
      active = false;
    };
  }, [userId]);

  const lessonCards = useMemo(
    () =>
      lessons.map((lesson) => ({
        ...lesson,
        stats: getLessonStats(lesson),
      })),
    [lessons],
  );

  if (activeLesson?.lessonId && activeLesson?.blockId) {
    return (
      <LessonScreen
        lessonId={activeLesson.lessonId}
        blockId={activeLesson.blockId}
        userId={userId}
        allowGrammarHints={true}
        onExit={(points, phrases) => {
          setActiveLesson(null);
          onComplete?.(points, phrases);
        }}
        audioDisabled={audioDisabled}
      />
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.topBar}>
        <button className={styles.backButton} onClick={onBack}>
          {copy.back}
        </button>
      </div>

      {loading ? (
        <div className={styles.loading}>{copy.loading}</div>
      ) : lessonCards.length === 0 ? (
        <div className={styles.empty}>{copy.empty}</div>
      ) : (
        <div className={styles.lessonList}>
          {lessonCards.map((lesson) => {
            const isExpanded = expandedLessonId === lesson.id;
            const lessonImage = TOPIC_IMAGES[lesson.code] || null;
            const blocks = Array.isArray(lesson.blocks) ? lesson.blocks : [];

            return (
              <section
                className={`${styles.lessonCard} ${isExpanded ? styles.lessonCardExpanded : ''}`}
                key={lesson.id}
              >
                <button
                  className={styles.lessonToggle}
                  onClick={() => setExpandedLessonId((prev) => (prev === lesson.id ? null : lesson.id))}
                >
                  <div className={styles.lessonVisual}>
                    {lessonImage ? (
                      <div
                        className={styles.lessonVisualImage}
                        style={{ backgroundImage: `url(${lessonImage})` }}
                      />
                    ) : (
                      <div className={styles.lessonVisualFallback}>
                        <BookOpenText size={28} strokeWidth={2.1} />
                      </div>
                    )}
                    <span className={styles.lessonVisualBadge}>
                      {lesson.stats.completedBlocks}/{lesson.stats.totalBlocks || 0}
                    </span>
                  </div>

                  <div className={styles.lessonMeta}>
                    <div className={styles.lessonTitleRow}>
                      <h2 className={styles.lessonTitle}>{lesson.title}</h2>
                      {lesson.stats.hasProgress && (
                        <span className={styles.lessonState}>
                          {lesson.stats.isCompleted ? copy.completed : copy.continue}
                        </span>
                      )}
                    </div>
                    <div className={styles.lessonSubtitle}>
                      {copy.progress}: {lesson.stats.completedBlocks}/{lesson.stats.totalBlocks || 0}
                    </div>
                    <div className={styles.progressTrack}>
                      <div
                        className={styles.progressFill}
                        style={{ width: `${lesson.stats.percent}%` }}
                      />
                    </div>
                  </div>

                  <div className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ''}`}>
                    <ChevronDown size={20} strokeWidth={2.3} />
                  </div>
                </button>

                {isExpanded && (
                  <div className={styles.lessonBody}>
                    {lesson.stats.nextBlock && (
                      <div className={styles.resumeRow}>
                        <div className={styles.resumeHint}>{copy.resume}</div>
                        <button
                          className={styles.resumeButton}
                          onClick={() =>
                            setActiveLesson({
                              lessonId: lesson.id,
                              blockId: lesson.stats.nextBlock.id,
                            })
                          }
                        >
                          {lesson.stats.hasProgress && !lesson.stats.isCompleted ? copy.continue : copy.start}
                        </button>
                      </div>
                    )}

                    <div className={styles.blockGrid}>
                      {blocks.map((block) => {
                        const completionCount = Number(lesson.completed_block_ids?.[block.id]) || 0;
                        const completionTier = getBlockTier(completionCount);
                        const tierLabel = getTierLabel(completionTier, copy);
                        const isNextBlock =
                          !lesson.stats.isCompleted && lesson.stats.nextBlock?.id === block.id;
                        const statusLabel = completionTier
                          ? `${tierLabel}${completionCount > 3 ? ` x${completionCount}` : ''}`
                          : (isNextBlock ? copy.continue : copy.start);

                        return (
                          <button
                            className={`${styles.blockButton} ${isNextBlock ? styles.blockButtonRecommended : ''}`}
                            key={block.id}
                            onClick={() => setActiveLesson({ lessonId: lesson.id, blockId: block.id })}
                          >
                            <div className={styles.blockHeader}>
                              <div className={styles.blockTitle}>{block.title || copy.title}</div>
                              <span
                                className={`${styles.blockBadge} ${
                                  completionTier === 'bronze'
                                    ? styles.blockBadgeBronze
                                    : completionTier === 'silver'
                                      ? styles.blockBadgeSilver
                                      : completionTier === 'gold'
                                        ? styles.blockBadgeGold
                                        : ''
                                }`}
                              >
                                {statusLabel}
                              </span>
                            </div>
                            <div className={styles.blockMeta}>
                              <span>
                                #{Number(block.sort_order) || 0}
                              </span>
                              {completionCount > 0 && (
                                <span>
                                  x{completionCount}
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PathLessons;
